import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  fetchApplicationInfo,
  fetchRentalList,
  fetchResidentDefault,
  saveResidentDefault,
  updateClaimLock,
  updateExcludeAuto,
} from '../services/residentApi'
import type {
  PaymentReviewFormRow,
  ResidentApplicationData,
  ResidentDefaultFormValues,
  ResidentDetailResponse,
  ResidentMessageFormValues,
  ResidentNote,
  RentalListResponse,
} from '../types'
import { mergeApplicationProperty } from '../utils/residentApplicationProperty'
import {
  applyDocumentStatusesToFormValues,
  normalizeResidentDetailDocuments,
} from '../utils/residentDocuments'
import { normalizeResidentDefaultFormValues, resolveLeaseMonthFromDetail } from '../utils/residentPayload'
import {
  liftCoResidentProgressStatus,
  resolveActiveCoResident,
} from '../utils/resolveActiveCoResident'
import { isFinalLedgerLockedFlag } from '../utils/residentValidation'
import {
  hydrateLossAdjustmentAmount,
  toYmd,
} from '@/features/reimbursements/utils/detailFormHydration'
import {
  RESIDENT_DEFAULT_FORM_VALUES,
  RESIDENT_MESSAGE_FORM_VALUES,
} from '../constants/detailFormDefaults'

function normalizeResidentNotes(value: unknown): ResidentNote[] | undefined {
  if (value === undefined || value === null) return undefined
  if (Array.isArray(value)) return value as ResidentNote[]
  if (typeof value === 'object') return Object.values(value) as ResidentNote[]
  return undefined
}

export function toFormValues(
  detail?: ResidentDetailResponse | null,
  applicationData?: ResidentApplicationData | null,
  rentalList?: RentalListResponse | null,
): ResidentDefaultFormValues {
  const residentDefault = (detail?.resident_default || {}) as Record<string, unknown>
  const unitKey = (detail?.unit_key || {}) as Record<string, unknown>
  const initialValues = Object.keys(RESIDENT_DEFAULT_FORM_VALUES).reduce((acc, key) => {
    const defaultValue = RESIDENT_DEFAULT_FORM_VALUES[key as keyof ResidentDefaultFormValues]
    const value = residentDefault[key] ?? unitKey[key]
    ;(acc as Record<string, string>)[key] = value === undefined || value === null ? defaultValue : String(value)
    return acc
  }, { ...RESIDENT_DEFAULT_FORM_VALUES })

  // API stores combined `first_month_not_paid` (YYYY-MM-DD); Angular `setResidentData` splits it into month/year.
  const combinedFirstMonth = residentDefault.first_month_not_paid
  if (combinedFirstMonth !== undefined && combinedFirstMonth !== null && String(combinedFirstMonth).trim() !== '') {
    ;(initialValues as Record<string, unknown>).first_month_not_paid = String(combinedFirstMonth)
  }

  const resolvedLeaseMonth = resolveLeaseMonthFromDetail(detail)
  if (resolvedLeaseMonth !== '') {
    initialValues.lease_month = resolvedLeaseMonth
  }

  // API may send is_final_ledger as 1 / true / '1'; keep form + checkbox in sync.
  if (isFinalLedgerLockedFlag(initialValues.is_final_ledger) || isFinalLedgerLockedFlag(residentDefault.is_final_ledger)) {
    initialValues.is_final_ledger = '1'
  } else if (
    initialValues.is_final_ledger === '0' ||
    residentDefault.is_final_ledger === '0' ||
    residentDefault.is_final_ledger === 0
  ) {
    initialValues.is_final_ledger = ''
  }

  // Angular setResidentData: EVICTION records normalize skip_claim_affidavit_required to '1' if '1', else '0'.
  // API may return numeric 1/0 — coerce before comparing so Yes survives reload after save/upload.
  if (
    residentDefault.rental_status === 'EVICTION' ||
    unitKey.rental_status === 'EVICTION'
  ) {
    initialValues.skip_claim_affidavit_required =
      String(residentDefault.skip_claim_affidavit_required ?? '') === '1' ||
      String(unitKey.skip_claim_affidavit_required ?? '') === '1'
        ? '1'
        : '0'
  }

  // Angular setResidentData: empty loss adjustments display as $0.00 (never blank).
  initialValues.unused_security_deposit = hydrateLossAdjustmentAmount(
    residentDefault.unused_security_deposit,
  )
  initialValues.partial_payment_amount = hydrateLossAdjustmentAmount(
    residentDefault.partial_payment_amount,
  )
  initialValues.loss_adjustment = hydrateLossAdjustmentAmount(residentDefault.loss_adjustment)

  // Prefer persisted DB end date in DatePicker format (Angular uses validateData on load).
  const apiEndDate = toYmd(residentDefault.adjust_max_movein_date)
  if (apiEndDate) {
    initialValues.adjust_max_movein_date = apiEndDate
  }

  const statusMap = rentalList?.rental_status || {}
  const reasonMap = rentalList?.reason || {}

  const normalizeToken = (value: unknown) =>
    String(value || '')
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')

  const statusEntries = Object.entries(statusMap)
  const reasonEntries = Object.entries(reasonMap)

  const mapStatusKey = (value?: string) => {
    if (!value) return ''
    if (statusMap[value]) return value
    const target = normalizeToken(value)
    for (const [key, item] of statusEntries) {
      if (normalizeToken(key) === target || normalizeToken(item?.name) === target) return key
    }
    return target
  }

  const mapReasonKey = (value?: string) => {
    if (!value) return ''
    if (reasonMap[value]) return value
    const target = normalizeToken(value)
    for (const [key, label] of reasonEntries) {
      if (normalizeToken(key) === target || normalizeToken(label) === target) return key
    }
    return target
  }

  const mapProgressKey = (value?: string, statusKey?: string) => {
    if (!value || !statusKey) return value || ''
    const resolvedStatusKey = statusMap[statusKey]
      ? statusKey
      : statusEntries.find(([key]) => normalizeToken(key) === normalizeToken(statusKey))?.[0] || statusKey
    const progressMap = statusMap[resolvedStatusKey]?.progress || {}
    if (progressMap[value]) return value
    const target = normalizeToken(value)
    for (const [key, label] of Object.entries(progressMap)) {
      if (normalizeToken(key) === target || normalizeToken(label) === target) return key
    }
    return target
  }

  const fallbackStatus = mapStatusKey(applicationData?.rental_status)
  const fallbackReason = mapReasonKey(applicationData?.reason_for_default)
  const fallbackProgress = mapProgressKey(applicationData?.rental_progress, fallbackStatus)

  const normalized = normalizeResidentDefaultFormValues(
    initialValues,
    applicationData?.contract_start_date,
    applicationData?.contract_end_date,
    applicationData?.unit_number,
    fallbackStatus,
    fallbackProgress,
    fallbackReason,
  )

  const currentStatus = mapStatusKey(normalized.rental_status)
  const currentProgress = mapProgressKey(normalized.rental_progress, currentStatus)
  const currentReason = mapReasonKey(normalized.reason_for_default)
  return applyDocumentStatusesToFormValues(
    {
      ...normalized,
      rental_status: currentStatus,
      rental_progress: currentProgress,
      reason_for_default: currentReason,
    },
    detail,
  )
}

function toMessageValues(detail?: ResidentDetailResponse | null): ResidentMessageFormValues {
  const residentDefault = (detail?.resident_default || {}) as Record<string, unknown>
  return Object.keys(RESIDENT_MESSAGE_FORM_VALUES).reduce((acc, key) => {
    const defaultValue = RESIDENT_MESSAGE_FORM_VALUES[key as keyof ResidentMessageFormValues]
    const value = residentDefault[key]
    ;(acc as Record<string, string>)[key] = value === undefined || value === null ? defaultValue : String(value)
    return acc
  }, { ...RESIDENT_MESSAGE_FORM_VALUES })
}

function normalizeApplicationData(
  application: ResidentApplicationData,
  routeApplicantId?: string,
  routeUserId?: string,
): ResidentApplicationData {
  // Angular: `applicationData = applicationAllData.co_resident_application[applicantId]`
  // — progress_status (CERTIFICATE_SENT) lives on that co-resident row.
  const activeCoResident = resolveActiveCoResident(application, routeApplicantId, routeUserId)
  const withProgress = liftCoResidentProgressStatus(application, routeApplicantId, routeUserId)
  const fallbackUserData: ResidentApplicationData['user_data'] =
    application.user_data ||
    activeCoResident?.user_data ||
    (application.first_name || application.last_name || application.email || application.mobile_no
      ? {
          first_name: application.first_name,
          last_name: application.last_name,
          email: application.email,
          mobile_no: application.mobile_no,
        }
      : undefined)

  return mergeApplicationProperty({
    ...withProgress,
    user_data: fallbackUserData,
    contract_start_date: application.contract_start_date || application.lease_start_date,
    contract_end_date: application.contract_end_date || application.lease_end_date,
  })
}

export function useResidentDetail(applicantId?: string, userId?: string) {
  const [residentDetail, setResidentDetail] = useState<ResidentDetailResponse | null>(null)
  const [applicationData, setApplicationData] = useState<ResidentApplicationData | null>(null)
  const [rentalList, setRentalList] = useState<RentalListResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!applicantId || !userId) return null
    if (!options?.silent) setLoading(true)
    setError(null)
    try {
      const [application, rentals] = await Promise.all([
        fetchApplicationInfo(applicantId),
        fetchRentalList().catch(() => ({
          rental_status: {},
          reason: {},
        })),
      ])
      const rawDetail = await fetchResidentDefault(userId, application?.application_id, {
        reimbursement_detail: true,
      })
      const normalizedDetail = normalizeResidentDetailDocuments(rawDetail)
      const normalizedApplication = normalizeApplicationData(application, applicantId, userId)
      setResidentDetail(normalizedDetail)
      setApplicationData(normalizedApplication)
      setRentalList(rentals)
      return {
        residentDetail: normalizedDetail,
        applicationData: normalizedApplication,
        rentalList: rentals,
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load resident detail')
      return null
    } finally {
      if (!options?.silent) setLoading(false)
    }
  }, [applicantId, userId])

  useEffect(() => {
    void load()
  }, [load])

  const formValues = useMemo(
    () => toFormValues(residentDetail, applicationData, rentalList),
    [applicationData, residentDetail, rentalList],
  )
  const messageValues = useMemo(() => toMessageValues(residentDetail), [residentDetail])
  const paymentRows = useMemo<PaymentReviewFormRow[]>(
    () => residentDetail?.payment_review_log || [],
    [residentDetail?.payment_review_log],
  )

  const save = useCallback(
    async (values: Record<string, unknown> | FormData) => {
      if (!userId) return null
      setSaving(true)
      try {
        await saveResidentDefault(userId, values)
        // Angular `loadResidentDetail()` after save — POST body is often partial and must not drive tab visibility.
        return await load({ silent: true })
      } finally {
        setSaving(false)
      }
    },
    [load, userId],
  )

  const [toggleLockLoading, setToggleLockLoading] = useState(false)

  const toggleLock = useCallback(async (): Promise<unknown> => {
    const defaultId = residentDetail?.resident_default?.fk_resident_default_id
    if (!defaultId) return
    setToggleLockLoading(true)
    try {
      const response = await updateClaimLock(defaultId)
      await load({ silent: true })
      return response
    } finally {
      setToggleLockLoading(false)
    }
  }, [load, residentDetail?.resident_default?.fk_resident_default_id])

  const setExcludeAuto = useCallback(
    async (checked: boolean) => {
      const defaultId = residentDetail?.resident_default?.fk_resident_default_id
      if (!defaultId) return
      await updateExcludeAuto(defaultId, checked)
      await load({ silent: true })
    },
    [load, residentDetail?.resident_default?.fk_resident_default_id],
  )

  /** Merge fields onto `resident_default` without refetching GET /api/default. */
  const patchResidentDefault = useCallback((patch: Record<string, unknown>) => {
    setResidentDetail((prev) => {
      if (!prev?.resident_default) return prev
      return {
        ...prev,
        resident_default: {
          ...prev.resident_default,
          ...patch,
        },
      }
    })
  }, [])

  /** Update public message history after note/message POST without refetching the whole page. */
  const mergeMessagesFromResponse = useCallback(
    (response: ResidentDetailResponse, fallbackText?: string, fallbackAuthorName?: string) => {
      const raw = response as Record<string, unknown>
      let nextMessages = normalizeResidentNotes(raw.messages ?? raw.message)
      setResidentDetail((prev) => {
        if (!prev) return response
        if (nextMessages === undefined && fallbackText) {
          const existing = normalizeResidentNotes(prev.message ?? prev.messages) ?? []
          const author = String(fallbackAuthorName ?? '').trim()
          nextMessages = [
            {
              message: fallbackText,
              created_at: Math.floor(Date.now() / 1000),
              ...(author ? { name: author, sender_name: author } : {}),
            },
            ...existing,
          ]
        }
        if (nextMessages === undefined) return prev
        return { ...prev, message: nextMessages, messages: nextMessages }
      })
    },
    [],
  )

  return {
    residentDetail,
    applicationData,
    rentalList,
    loading,
    saving,
    error,
    formValues,
    messageValues,
    paymentRows,
    load,
    save,
    toggleLock,
    toggleLockLoading,
    setExcludeAuto,
    mergeMessagesFromResponse,
    patchResidentDefault,
  }
}
